import { trackingAllowed } from './analyticsPolicy';
// Facebook Pixel Service for tracking custom events
declare global {
  interface Window {
    fbq: any;
  }
}

export class FacebookPixelService {
  private static instance: FacebookPixelService;
  private isInitialized: boolean = false;

  private constructor() {}

  public static getInstance(): FacebookPixelService {
    if (!FacebookPixelService.instance) {
      FacebookPixelService.instance = new FacebookPixelService();
    }
    return FacebookPixelService.instance;
  }

  // Initialize the pixel (called after page load)
  public init(): void {
    if (this.isInitialized || typeof window === 'undefined' || !trackingAllowed() || !window.fbq) {
      return;
    }

    try {
      this.isInitialized = true;
      console.log('✅ Facebook Pixel initialized successfully');
    } catch (error) {
      console.error('❌ Facebook Pixel initialization failed:', error);
    }
  }

  // Track custom events
  public trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (typeof window === 'undefined' || !trackingAllowed() || !window.fbq) {
      console.warn('Facebook Pixel not available');
      return;
    }

    try {
      // Do not send financial amounts or user-entered text to advertising tools.
      // Preserve existing SignUp custom conversion; custom names need trackCustom.
      const standard = ['PageView', 'CompleteRegistration', 'InitiateCheckout', 'Purchase', 'Subscribe', 'Lead'];
      window.fbq(standard.includes(eventName) ? 'track' : 'trackCustom', eventName);
    } catch (error) {
      console.error(`❌ Facebook Pixel: Failed to track ${eventName}:`, error);
    }
  }

  // Track custom Facebook Pixel events
  public trackCustomEvent(eventName: string, parameters?: Record<string, any>): void {
    if (typeof window === 'undefined' || !trackingAllowed() || !window.fbq) {
      console.warn('Facebook Pixel not available');
      return;
    }

    try {
      window.fbq('trackCustom', eventName);
    } catch (error) {
      console.error(`❌ Facebook Pixel: Failed to track custom ${eventName}:`, error);
    }
  }

  public trackSignupStarted(source?: string): void {
    this.trackCustomEvent('SignupStarted', source ? { source } : undefined);
  }

  public trackSignupFormViewed(source?: string): void {
    this.trackCustomEvent('SignupFormViewed', {
      funnel: 'activation',
      ...(source && { source })
    });
  }

  public trackSignupError(source: string, reason: string): void {
    this.trackCustomEvent('SignupError', {
      funnel: 'activation',
      source,
      reason
    });
  }

  // Track goal-related events
  public trackGoalCreated(goalName: string, targetAmount: number, category: string): void {
    this.trackEvent('GoalCreated', {
      goal_name: goalName,
      target_amount: targetAmount,
      category: category,
      currency: 'USD'
    });
  }

  public trackGoalUpdated(goalName: string, savedAmount: number, progress: number): void {
    this.trackEvent('GoalUpdated', {
      goal_name: goalName,
      saved_amount: savedAmount,
      progress_percentage: progress,
      currency: 'USD'
    });
  }

  public trackGoalCompleted(goalName: string, totalAmount: number, timeToComplete: number): void {
    this.trackEvent('GoalCompleted', {
      goal_name: goalName,
      total_amount: totalAmount,
      time_to_complete_days: timeToComplete,
      currency: 'USD'
    });
  }

  public trackPaymentMade(amount: number, goalName?: string): void {
    this.trackEvent('PaymentMade', {
      amount: amount,
      goal_name: goalName || 'General',
      currency: 'USD'
    });
  }

  // Track user engagement
  public trackAppOpened(): void {
    this.trackEvent('AppOpened');
  }

  public trackDashboardViewed(): void {
    this.trackEvent('DashboardViewed');
  }

  public trackExpenseAdded(amount: number, category: string): void {
    this.trackEvent('ExpenseAdded', {
      amount: amount,
      category: category,
      currency: 'USD'
    });
  }

  public trackCategoryViewed(categoryName: string): void {
    this.trackEvent('CategoryViewed', {
      category_name: categoryName
    });
  }

  // Track conversion events
  public trackSignUp(method: string): void {
    this.trackEvent('SignUp', {
      method: method
    });
  }

  public trackLogin(method: string): void {
    this.trackEvent('Login', {
      method: method
    });
  }

  public trackPremiumUpgrade(plan: string, source?: string): void {
    this.trackEvent('PremiumUpgrade', {
      plan: plan,
      ...(source && { source })
    });
  }

  /** Funnel: Monitoring. Use for % click CTA, % open upgrade modal, % convert, % complete connection. */
  public trackMonitoringCtaClick(): void {
    this.trackEvent('MonitoringCtaClick', { funnel: 'automatic_monitoring' });
  }

  public trackMonitoringUpgradeModalOpen(): void {
    this.trackEvent('MonitoringUpgradeModalOpen', { funnel: 'automatic_monitoring' });
  }

  public trackMonitoringUpgradeStarted(): void {
    this.trackEvent('MonitoringUpgradeStarted', { funnel: 'automatic_monitoring' });
  }

  public trackMonitoringConnectionComplete(insightCount?: number): void {
    this.trackEvent('MonitoringConnectionComplete', {
      funnel: 'automatic_monitoring',
      ...(typeof insightCount === 'number' && { insight_count: insightCount })
    });
  }

  public trackOnboardingViewed(): void {
    this.trackCustomEvent('OnboardingViewed', { funnel: 'activation' });
  }

  public trackOnboardingStepViewed(step: number, stepName: string): void {
    this.trackCustomEvent('OnboardingStepViewed', {
      funnel: 'activation',
      step,
      step_name: stepName
    });
  }

  public trackOnboardingChoice(choice: 'connect_bank' | 'demo_snapshot' | 'manual_setup' | 'skip'): void {
    this.trackCustomEvent('OnboardingChoiceSelected', {
      funnel: 'activation',
      choice
    });
  }

  public trackOnboardingCompleted(source: string): void {
    this.trackCustomEvent('OnboardingCompleted', {
      funnel: 'activation',
      source
    });
  }

  public trackEmptyDashboardViewed(source?: string): void {
    this.trackCustomEvent('EmptyDashboardViewed', {
      funnel: 'activation',
      ...(source && { source })
    });
  }

  public trackFirstValueSeen(source: string): void {
    this.trackCustomEvent('FirstValueSeen', {
      funnel: 'activation',
      source
    });
  }

  // Track app performance
  public trackAppError(errorType: string, errorMessage: string): void {
    this.trackEvent('AppError', {
      error_type: errorType,
      error_message: errorMessage
    });
  }

  public trackFeatureUsed(featureName: string): void {
    this.trackEvent('FeatureUsed', {
      feature_name: featureName
    });
  }
}

// Export singleton instance
export const facebookPixel = FacebookPixelService.getInstance();
